/**
 * @fileoverview Componente Organismo para la gestión y visualización de recordatorios de clientes en el módulo Inbox.
 * @module CustomerReminderMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.3.0
 *
 * @description
 * Componente Organismo que muestra la lista de recordatorios programados para un cliente específico en el Inbox.
 * Implementa composición atómica, integración con hooks de Data/UI Layer, y optimización de renderizado con React.memo y useMemo.
 *
 * **Características:**
 * - Visualización de recordatorios programados
 * - Botón para agendar nuevo recordatorio
 * - Estado de carga y pantalla vacía
 * - Integración con i18n y componentes globales
 * - Debug tracking de renders y dependencias
 *
 * **Atomic Design:**
 * - Nivel: Organism
 * - Composición: CustomerReminderItemMC (molecule), ButtonMC (atom), EmptyScreenListInboxMC (molecule), LoadingMC (atom)
 * - Dependencias: useCustomerRemindersData (Data Layer), useCustomerRemindersUI (UI Layer)
 *
 * @typedef {Object} CustomerReminderMCProps
 * @property {Object} customerData - Datos del cliente para el cual se muestran los recordatorios
 *
 * @param {CustomerReminderMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado
 *
 * @example
 * <CustomerReminderMC customerData={customer} />
 *
 * @see {@link useCustomerRemindersData} Hook de Data Layer para cargar recordatorios
 * @see {@link useCustomerRemindersUI} Hook de UI Layer para gestionar modales
 * @see {@link CustomerReminderItemMC} Componente hijo para cada recordatorio
 * @see {@link ButtonMC} Botón global
 * @see {@link EmptyScreenListInboxMC} Pantalla vacía
 * @see {@link LoadingMC} Indicador de carga
 */
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingMC from '../../components/LoadingMC';
import ButtonComponent from '../../components/ButtonMC';
import EmptyScreenListInboxMC from '../../components/EmptyScreenListInboxMC';
import useCustomerRemindersData from '../../hooks/inbox/data/useCustomerRemindersData';
import useCustomerRemindersUI from '../../hooks/inbox/ui/useCustomerRemindersUI';
import { CustomerReminderItemMC } from './components';
import { useTraceUpdate } from '../../hooks/shared/useDebugRenders';

/**
 * Componente principal para mostrar y gestionar los recordatorios de cliente en Inbox.
 *
 * @function CustomerReminderMC
 * @memberof module:CustomerReminderMC
 * @param {CustomerReminderMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado
 */
const CustomerReminderMC = memo(({ customerData }) => {
  const { t } = useTranslation('InboxMC');
  // Hook Data Layer: carga y gestión de recordatorios
  const { loading, customerReminders, setCustomerReminders } = useCustomerRemindersData(customerData);
  // Hook UI Layer: gestión de modales y acciones UI
  const { openDealModal } = useCustomerRemindersUI(customerData);

  /**
   * Memoización de la lista de recordatorios para optimizar renderizados.
   * @memberof module:CustomerReminderMC
   * @returns {JSX.Element[]} Elementos JSX de recordatorios
   */
  const remindersList = useMemo(() => (
    customerReminders.map((reminder) => (
      <div key={`customer-order-${reminder.id}`} className="tw-w-full tw-mb-4">
        <CustomerReminderItemMC reminder={reminder} setCustomerReminders={setCustomerReminders} />
      </div>
    ))
  ), [customerReminders, setCustomerReminders]);

  // Debug tracking de dependencias críticas y renderizados
  useTraceUpdate('CustomerReminderMC', {
    customerId: customerData?.id,
    loading,
    remindersCount: customerReminders.length,
    setCustomerRemindersRef: setCustomerReminders,
    openDealModalRef: openDealModal
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  // Estado de carga
  if (loading) return <LoadingMC loading={loading} />;

  // Render principal
  return (
    <div className="tw-wrapper">
      <div className="tw-flex tw-flex-col tw-h-[calc(100vh-250px)] tw-max-h-screen tw-min-h-[400px]">
        <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-pr-1">
          <span className="tw-text-sm tw-font-medium tw-text-gray-950">
            {t('reminders.scheduled_messages')}
          </span>
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
            <ButtonComponent
              outlined
              text={t('reminders.schedule_message')}
              onClick={openDealModal}
              className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-my-2"
            />

            {customerReminders.length === 0 && !loading && (
              <EmptyScreenListInboxMC
                iconName="flash-outline"
                showDescription={false}
                title={t('reminders.no_scheduled')}
                iconClassName="!tw-size-[24px]"
                containerClassName="tw-w-full"
                innerClassName="!tw-rounded-xl !tw-p-6"
              />
            )}

            {remindersList}
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Nombre de display para el componente CustomerReminderMC
 * @type {string}
 */
CustomerReminderMC.displayName = 'CustomerReminderMC';

export default CustomerReminderMC;